import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AlgoliaKeywordSearch, AlgoliaLocationSearch } from '../../../../components';
import { createResourceLocatorString } from '../../../../util/routes';
import moment from 'moment';
import { START_DATE } from '../../../../util/dates';
import { DateRangeInput } from '../../../../components/FieldDateRangeInput/FieldDateRangeInput';
import AlgoliaFilters from '../FilterComponent/AlgoliaFilters';
import { manageDisableScrolling } from '../../../../ducks/ui.duck';
import { useLocationBasedPlaceholder } from '../../../../hooks/useLocationBasedPlaceholder';
import { useHasUserInteracted } from '../../../../hooks/useHasUserInteracted';
import { saveRentalDefaults, pickRentalDefaultsFromValues } from '../../../../util/rentalDefaults';
import { getAlgoliaIndexNameForLang } from '../../../../util/urlHelpers';
import { SUPPORTED_LANGUAGES } from '../../../../util/constants';
import { loadAlgoliaInsights } from '../../../../analytics/handlers';
import classNames from 'classnames';
import css from './DesktopSearchBar.module.css';

const moreFilters = [
  { attribute: 'publicData.access_to_rentals', label: 'Access' },
  { attribute: 'publicData.Condition_of_the_item', label: 'Condition' },
  { attribute: 'publicData.Trust_and_verification', label: 'Trust' },
  { attribute: 'publicData.cancelation_policy', label: 'Cancellation' },
  { attribute: 'publicData.reason_for_listing', label: 'Reason' },
  { attribute: 'publicData.security_deposit', label: 'Deposit' },
];

const allFilterAttributes = [
  { attribute: 'publicData.category', label: 'Category' },
  ...moreFilters,
];

const getInitialValue = (attribute, params, options = []) => {
  if (!attribute) return null;
  if (attribute === 'price.amount' && params['priceRange']?.split(',').length > 1) {
    const min = +params['priceRange'].split(',')[0] / 100;
    const max = +params['priceRange'].split(',')[1] / 100;
    return [min, max];
  }
  const value = typeof params === 'object' ? params[attribute] : null;
  return options.find(o => o.value == value) || null;
};

function omitProperties(obj, keysToOmit) {
  return Object.keys(obj).reduce((result, key) => {
    if (!keysToOmit.includes(key)) result[key] = obj[key];
    return result;
  }, {});
}

const formatFacetValue = value => {
  const str = String(value);
  const needsQuotes = /\s|:|"/.test(str);
  const escaped = str.replace(/"/g, '\\"');
  return needsQuotes ? `"${escaped}"` : escaped;
};

const buildFilterPayload = selectedFilters =>
  Object.entries(selectedFilters || {})
    .filter(([, v]) => v !== null && v !== undefined && `${v}`.length > 0)
    .map(([key, value]) => `publicData.${key}:${formatFacetValue(value)}`);

export default function DesktopSearchBar(props) {
  const {
    queryParams,
    history,
    routesConfiguration,
    config,
    intl,
    currentUserLocationInfo,
    selectedBounds,
    onActivateListing,
    algoliaListings,
    algoliaStores,
    algoliaQueryInProgress,
    currentUserCurrency,
    currentUser,
    exchangeRates,
    facets,
    facetsError,
    facetsInProgress,
    showMap,
    locale,
    defaultSort,
    hasLocationAvailable,
    isMapExploring,
    onLocationSelection,
    dateRange = {},
    onDateRangeChange,
  } = props;

  const {
    keyword: qKeyword,
    bounds: qBounds,
    address: qAddress,
  } = queryParams || {};
  // Dates live in parent React state (not URL) to prevent loadData re-fires
  const qStartDate = dateRange?.startDate || null;
  const qEndDate = dateRange?.endDate || null;

  const [pendingStartDate, setPendingStartDate] = useState(null);
  const [pendingEndDate, setPendingEndDate] = useState(null);
  const [focusedDateInput, setFocusedDateInput] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [forceShowDateRow, setForceShowDateRow] = useState(false);
  const searchBarRef = useRef(null);
  const hasUserInteractedRef = useHasUserInteracted();

  useEffect(() => {
    const container = searchBarRef.current;
    if (!container?.parentNode) return;

    const topbarHeight =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--topbarHeightDesktop')
      ) || 56;

    // When rows are conditionally hidden in sticky mode the search bar's height
    // shrinks, shifting cards upward. The browser's scroll-anchoring algorithm
    // then tries to maintain the visual position of those cards by decreasing
    // window.scrollY — which pulls the sentinel back into the viewport, flips
    // isSticky to false, rows reappear, height grows, browser re-anchors, and
    // the cycle repeats at 60 fps (the flicker + "won't scroll" symptom).
    // Opting the parent column out of scroll anchoring breaks the loop entirely.
    const leftColumn = container.parentNode;
    leftColumn.style.overflowAnchor = 'none';

    // A zero-layout-impact sentinel placed BEFORE the search bar in the DOM.
    // Its viewport position depends only on window.scrollY — conditional row
    // rendering inside the bar can never affect it.
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText =
      'height: 1px; margin-top: -1px; overflow: hidden; visibility: hidden; pointer-events: none;';
    container.parentNode.insertBefore(sentinel, container);

    const observer = new IntersectionObserver(
      ([entry]) => {
        const nextSticky = !entry.isIntersecting;
        setIsSticky(prev => (prev === nextSticky ? prev : nextSticky));
      },
      { rootMargin: `-${topbarHeight + 1}px 0px 0px 0px`, threshold: 0 }
    );

    observer.observe(sentinel);

    return () => {
      leftColumn.style.overflowAnchor = '';
      observer.disconnect();
      if (sentinel.parentNode) sentinel.remove();
    };
  }, []);

  // Expand the date row in-place when triggered by "Change Dates" in the rates sheet.
  // Dispatching 'ras:openDatePicker' avoids prop-drilling and page scroll.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => {
      setForceShowDateRow(true);
      setTimeout(() => {
        const el = document.getElementById('desktopStartDate');
        if (el) { el.focus(); el.click(); }
      }, 350);
    };
    window.addEventListener('ras:openDatePicker', handler);
    return () => window.removeEventListener('ras:openDatePicker', handler);
  }, []);

  // Collapse date row back once the calendar closes (user applied or dismissed)
  useEffect(() => {
    if (!focusedDateInput) {
      setForceShowDateRow(false);
    }
  }, [focusedDateInput]);

  const queryParamsRef = useRef(queryParams);
  useEffect(() => {
    queryParamsRef.current = queryParams;
  }, [queryParams]);

  // Sync pending dates from parent dateRange state
  useEffect(() => {
    setPendingStartDate(qStartDate ? moment(qStartDate, 'YYYY-MM-DD').startOf('day').toDate() : null);
    setPendingEndDate(qEndDate ? moment(qEndDate, 'YYYY-MM-DD').startOf('day').toDate() : null);
  }, [qStartDate, qEndDate]);

  const dynamicPlaceholder = useLocationBasedPlaceholder('AlgoliaKeywordSearch.placeholder');
  // The landing page hero search is just a gateway to this page — when a tap
  // there (on the input or the mic) sent the user here, drop them straight
  // into the keyword field instead of making them click it again.
  const cameFromLandingPage = !!history?.location?.state?.fromLandingPage;

  const handleRefineLocation = location => {
    if (
      location &&
      location.address &&
      location.bounds &&
      (!history.location.state?.fromLandingPage || !location.fromLandingPage)
    ) {
      delete location.isLandingPage;
      if (onLocationSelection) onLocationSelection();
      history.push(
        createResourceLocatorString('SearchPage', routesConfiguration, {}, {
          ...queryParams,
          ...location,
        })
      );
    }
  };

  const handleRefineKeyword = useCallback(
    (keyword = '') => {
      history.push(
        createResourceLocatorString('SearchPage', routesConfiguration, {}, {
          ...queryParamsRef.current,
          keyword: keyword || '',
        })
      );
    },
    [history, routesConfiguration]
  );

  const handleDatesChange = ({ startDate, endDate }) => {
    setPendingStartDate(startDate || null);
    setPendingEndDate(endDate || null);
  };

  const handleApplyDates = () => {
    if (pendingStartDate && pendingEndDate) {
      // Store dates in parent state (NOT in URL) to prevent loadData re-fires,
      // Algolia re-queries, and scroll-to-top on date selection.
      if (onDateRangeChange) {
        onDateRangeChange({
          startDate: moment(pendingStartDate).format('YYYY-MM-DD'),
          endDate: moment(pendingEndDate).format('YYYY-MM-DD'),
        });
      }
      // Becomes the new default everywhere a date picker prefills from
      // util/rentalDefaults.js, even if this range is never booked - a genuine Apply
      // here is a real user gesture, unlike a prefill landing programmatically.
      saveRentalDefaults(
        pickRentalDefaultsFromValues({
          bookingDates: { startDate: pendingStartDate, endDate: pendingEndDate },
        })
      );
    }
    setFocusedDateInput(null);
  };

  const handleClearDates = () => {
    setPendingStartDate(null);
    setPendingEndDate(null);
    if (onDateRangeChange) {
      onDateRangeChange({ startDate: null, endDate: null });
    }
  };

  // Focusing Start Date means "re-pick the whole range", so both inputs are emptied and
  // the calendar stays open on Start Date - same behaviour as the listing page's
  // BookingDatesForm. The hasUserInteractedRef guard is there for the same reason it is
  // there: react-dates announces focus on its own while props settle after mount, and
  // treating that as a real gesture would wipe a prefill microseconds after it landed.
  const handleDateInputFocus = focusedInput => {
    setFocusedDateInput(focusedInput);
    if (
      focusedInput === START_DATE &&
      hasUserInteractedRef.current &&
      (pendingStartDate || pendingEndDate)
    ) {
      handleClearDates();
    }
  };

  // Compute translated sort options from config for the filters modal
  const sortOptions = (config?.search?.sortConfig?.options || []).map(option => {
    const label = option.labelTranslationKeyLong
      ? intl.formatMessage({ id: option.labelTranslationKeyLong })
      : option.labelTranslationKey
        ? intl.formatMessage({ id: option.labelTranslationKey })
        : (option.label || option.key);
    return { key: option.key, label };
  });

  const selectedMoreFilters = Object.keys(facets || {}).reduce((acc, key) => {
    const value = getInitialValue(key, queryParams, facets[key]);
    if (value) acc[key] = value;
    return acc;
  }, {});

  const handleShowRentals = selectedFilters => {
    const updatedParams = { ...(queryParams || {}) };
    Object.entries(selectedFilters).forEach(([attribute, values]) => {
      if (attribute === 'sort') {
        if (values) {
          updatedParams['sort'] = values;
        } else {
          delete updatedParams['sort'];
        }
      } else if (attribute === 'priceRange' && Array.isArray(values)) {
        if (values[0] !== 0 || values[1] !== 10000) {
          updatedParams['priceRange'] = `${values[0] * 100},${values[1] * 100}`;
        } else {
          delete updatedParams['priceRange'];
        }
      } else if (typeof values === 'string' || typeof values === 'number') {
        updatedParams[attribute] = values;
      }
    });
    history.push(createResourceLocatorString('SearchPage', routesConfiguration, {}, updatedParams));

    if (typeof window !== 'undefined') {
      const userToken = currentUser?.id?.uuid;
      if (userToken) {
        const indexName = getAlgoliaIndexNameForLang(locale || 'en', SUPPORTED_LANGUAGES);
        const filtersPayload = buildFilterPayload(selectedFilters);
        if (filtersPayload.length) {
          loadAlgoliaInsights().then(aa => {
            if (!aa || typeof aa !== 'function') return;
            aa('clickedFilters', { userToken, eventName: 'Filters Applied', index: indexName, filters: filtersPayload.slice(0, 20) });
            aa('viewedFilters', { userToken, eventName: 'Filters Viewed', index: indexName, filters: filtersPayload.slice(0, 20) });
          });
        }
      }
    }
    setIsOpen(false);
  };

  const handleClearFilters = () => {
    const filteredParams = omitProperties(
      queryParams,
      [...allFilterAttributes.map(f => f.attribute.split('.')[1]), 'priceRange']
    );
    history.push(createResourceLocatorString('SearchPage', routesConfiguration, {}, filteredParams));
  };

  const handleFilterClick = useCallback(
    (filterKey, value) => {
      const userToken = currentUser?.id?.uuid;
      if (!userToken || typeof window === 'undefined') return;
      const indexName = getAlgoliaIndexNameForLang(locale || 'en', SUPPORTED_LANGUAGES);
      loadAlgoliaInsights().then(aa => {
        if (!aa || typeof aa !== 'function') return;
        aa('clickedFilters', {
          userToken,
          eventName: 'Filter Clicked',
          index: indexName,
          filters: [`publicData.${filterKey}:${formatFacetValue(value)}`],
        });
      });
    },
    [currentUser, locale]
  );

  const hasActiveFilters = Object.keys(selectedMoreFilters).length > 0;

  // Hoist hook unconditionally — used in conditionally-rendered AlgoliaLocationSearch
  const memoizedRefineLocation = useCallback(l => handleRefineLocation(l), [queryParams]);

  // Determine which fields are still empty (committed to URL)
  const isDatesEmpty = !qStartDate || !qEndDate;
  const isLocationEmpty = !qAddress;
  const isKeywordEmpty = !qKeyword;
  const hasIncompleteFields = isDatesEmpty || isLocationEmpty || isKeywordEmpty;

  // Whether the bar is in the collapsed-all-filled state (sticky + all fields complete).
  // forceShowDateRow (triggered by "Change Dates") overrides collapse so the date row
  // expands in place without any page scroll.
  const isCollapsing = isSticky && !hasIncompleteFields && !forceShowDateRow;

  // Rows are always mounted for smooth CSS transition; rowCollapsed hides them when not needed
  const hideDateRow = isSticky && !isDatesEmpty && !isCollapsing && !forceShowDateRow;
  const hideLocationRow = isSticky && !isLocationEmpty && !isCollapsing;
  const hideKeywordRow = isSticky && !isKeywordEmpty && !isCollapsing;

  return (
    <div
      ref={searchBarRef}
      className={classNames(
        css.desktopSearchBar,
        isSticky && (hasIncompleteFields || forceShowDateRow) && css.sticky,
        isCollapsing && css.stickyHidden
      )}
    >
      {/* Row 1: Date range — always mounted; collapsed when sticky and dates are filled */}
      <div className={classNames(css.datePickers, isSticky && !isLocationEmpty && (!qStartDate && !qEndDate) && css.datesEmpty, isSticky && !isLocationEmpty && qStartDate && !qEndDate && css.endDateHighlight, (hideDateRow || isCollapsing) && css.rowCollapsed)}>
        <DateRangeInput
          value={{ startDate: pendingStartDate, endDate: pendingEndDate }}
          onChange={handleDatesChange}
          onFocus={handleDateInputFocus}
          onBlur={() => {}}
          onApply={handleApplyDates}
          onRemove={handleClearDates}
          isDaily={true}
          isDayBlocked={() => () => false}
          isOutsideRange={() => day => moment(day).isBefore(moment(), 'day')}
          minimumNights={0}
          focusedInput={focusedDateInput}
          startDateId="desktopStartDate"
          endDateId="desktopEndDate"
          startDatePlaceholderText={intl.formatMessage({ id: 'AlgoliaSearchSection.startDatePlaceholder' })}
          endDatePlaceholderText={intl.formatMessage({ id: 'AlgoliaSearchSection.endDatePlaceholder' })}
          firstDayOfWeek={config?.localization?.firstDayOfWeek || 0}
          hideKeyboardShortcutsPanel={true}
          showDefaultInputIcon={false}
          noBorder={true}
          block={true}
          withPortal={false}
          useMobileMargins={false}
          name="desktopDateRange"
        />
      </div>

      {/* Row 2: Location — always mounted; collapsed when sticky and location is filled */}
      <div className={classNames(css.locationRowWrapper, (hideLocationRow || isCollapsing) && css.rowCollapsed)}>
        <AlgoliaLocationSearch
          attribute={'publicData.location.address'}
          className={isLocationEmpty ? css.locationHighlight : undefined}
          defaultLocation={currentUserLocationInfo}
          isMapExploring={isMapExploring}
          onRefineLocation={memoizedRefineLocation}
          initialValues={{ bounds: qBounds, address: qAddress }}
          showInlineError={false}
        />
      </div>

      {/* Row 3: Keyword + Filters — always mounted; collapsed when sticky and keyword is filled */}
      <div className={classNames(css.keywordRow, !isLocationEmpty && !isDatesEmpty && isKeywordEmpty && css.keywordHighlight, (hideKeywordRow || isCollapsing) && css.rowCollapsed)}>
          <AlgoliaKeywordSearch
            onSearchChange={handleRefineKeyword}
            initialValue={{ keyword: qKeyword }}
            history={history}
            currentUserCurrency={currentUserCurrency}
            currentUser={currentUser}
            exchangeRates={exchangeRates}
            routes={routesConfiguration}
            config={config}
            intl={intl}
            clearKeywordsClass={css.clearKeywords}
            autoFocus={cameFromLandingPage}
            selectedBounds={selectedBounds}
            onActivateListing={onActivateListing}
            searchInputText={dynamicPlaceholder}
            algoliaListings={algoliaListings}
            algoliaStores={algoliaStores}
            algoliaQueryInProgress={algoliaQueryInProgress}
            showMap={showMap}
            locale={locale}
          />
          <div className={css.filtersButton}>
            {!facetsError && (
              <AlgoliaFilters
                facets={facets}
                manageDisableScrolling={manageDisableScrolling}
                handleShowRentals={handleShowRentals}
                setIsOpen={setIsOpen}
                isOpen={isOpen}
                selectedFilters={selectedMoreFilters}
                showResetButton={hasActiveFilters}
                onResetFilters={handleClearFilters}
                onFilterClick={handleFilterClick}
                sortOptions={sortOptions}
                currentSort={queryParams?.sort || defaultSort}
              />
            )}
          </div>
        </div>
    </div>
  );
}
